import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CardMedia,
} from "@mui/material";
import { Tokens } from "configs";

export interface DownloadMaterialCardProps {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  buttonText: string;
  onDownloadClick: () => void;
  ariaLabel: string;
  variant?: "presentation" | "pat";
  image?: string;
}

const CARD_HEIGHT = { xs: "auto", md: 376 };
const CARD_WIDTH = { xs: "100%", md: 392 };

export function DownloadMaterialCard({
  title,
  description,
  buttonText,
  onDownloadClick,
  ariaLabel,
  variant = "pat",
  image,
}: DownloadMaterialCardProps) {
  const isPresentationVariant = variant === "presentation";

  return (
    <Card
      sx={{
        backgroundColor: isPresentationVariant
          ? Tokens.neutralColor.light["1000"]
          : Tokens.interfaceColor.primary.pure,
        color: isPresentationVariant
          ? Tokens.neutralColor.dark["100"]
          : Tokens.neutralColor.light["1000"],
        borderRadius: isPresentationVariant ? 3 : 2.5,
        height: CARD_HEIGHT,
        width: CARD_WIDTH,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
      elevation={0}
    >
      {isPresentationVariant ? (
        <>
          {image && (
            <Box
              sx={{
                height: { xs: 120, md: 165 },
                width: "100%",
                backgroundColor: "#FED495",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <CardMedia
                component="img"
                image={image}
                alt=""
                sx={{
                  height: "100%",
                  width: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
          <CardContent
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              flex: 1,
              backgroundColor: Tokens.neutralColor.light["1000"],
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: 1.5,
                  mb: 0,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  fontWeight: 400,
                }}
              >
                {description}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={onDownloadClick}
              aria-label={ariaLabel}
              startIcon={
                <i
                  className="uil uil-import"
                  style={{ fontSize: 20, display: "flex" }}
                  aria-hidden="true"
                />
              }
              sx={{
                backgroundColor: Tokens.interfaceColor.primary.pure,
                color: Tokens.neutralColor.light["1000"],
                borderRadius: 2,
                textTransform: "none",
                fontSize: 16,
                fontWeight: 500,
                height: 48,
                px: 2.5,
                "&:hover": {
                  backgroundColor: Tokens.interfaceColor.primary.medium,
                },
              }}
            >
              {buttonText}
            </Button>
          </CardContent>
        </>
      ) : (
        <Box
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box sx={{ maxWidth: 300 }}>
            <Typography
              component="h3"
              sx={{
                fontWeight: 700,
                fontSize: 24,
                lineHeight: 1.2,
                mb: 1,
                color: Tokens.neutralColor.light["1000"],
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                lineHeight: 1.5,
                fontWeight: 400,
                color: Tokens.neutralColor.light["1000"],
              }}
            >
              {description}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={onDownloadClick}
            aria-label={ariaLabel}
            startIcon={
              <i
                className="uil uil-import"
                style={{ fontSize: 20, display: "flex" }}
                aria-hidden="true"
              />
            }
            sx={{
              backgroundColor: Tokens.neutralColor.light["800"],
              color: Tokens.neutralColor.dark["100"],
              borderRadius: 2,
              textTransform: "none",
              fontSize: 16,
              fontWeight: 500,
              height: 48,
              px: 2.5,
              alignSelf: "flex-start",
              "&:hover": {
                backgroundColor: Tokens.neutralColor.medium["700"],
              },
            }}
          >
            {buttonText}
          </Button>
        </Box>
      )}
    </Card>
  );
}
